/**
 * Shared upload-filtering helpers for Steeltooth.
 *
 * When a multi-page PDF is auto-split, the database keeps both the parent
 * row AND one child row per page. For any user-facing surface that "runs
 * AI" or "lets the user select source files", the parent becomes
 * redundant — it would double-feed the content into the LLM and clutter
 * the picker with an entry that's a superset of its siblings. The
 * backend de-dupes too (`routes/jobs.py::_expand_uploads_for_ai`), so
 * the UI and backend must agree on the same rule.
 *
 * Rule:
 *   A parent PDF is HIDDEN iff (split_status === "done") AND at least one
 *   child exists in the same list.
 *   Single-page parents, unsplit parents, and all children are KEPT.
 */

/** True when the upload is a parent whose children exist in ``all``. */
export function isSplitParentWithChildren(upload, all) {
    if (!upload || upload.parent_upload_id) return false;
    if (upload.split_status !== "done") return false;
    return (all || []).some((u) => u.parent_upload_id === upload.id);
}

/**
 * Return the list of uploads that should be offered as AI input options
 * (Analyze/Estimate/Takeoff dialogs). Drops split parents whose children
 * are in the list.
 */
export function visibleForAI(files) {
    const all = files || [];
    return all.filter((u) => !isSplitParentWithChildren(u, all));
}

/**
 * True when this upload should EXPOSE manual-edit UI (rename, annotate,
 * change scale). Split parents become read-only "containers" — to edit
 * content, the user works on the individual page children.
 */
export function isEditable(upload, all) {
    return !isSplitParentWithChildren(upload, all);
}
